import { createServerFn } from "@tanstack/react-start";
import { createClient } from "./supabase/admin";
import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { OrderTypes, Status } from "./supabase/enums";
import { queryOptions } from "@tanstack/react-query";
import {
  addMinutes,
  endOfDay,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isSameDay,
  isSameMinute,
  parseISO,
  set,
  startOfDay,
  subMinutes,
} from "date-fns";
import { z } from "zod";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

const timezone = "Europe/Amsterdam";

const getTimeKey = (utcDate: Date, type: string): string => {
  const zonedDate = toZonedTime(utcDate, timezone);
  return `${getHours(zonedDate)}:${getMinutes(zonedDate)}:${type}`;
};

export const shiftsQueryOptions = () =>
  queryOptions({
    queryKey: ["shifts"],
    queryFn: () => fetchShifts(),
  });

export const availabilityQueryKey = (date: Date) => ["availability", date];

export const availabilityQueryOptions = (date: Date) =>
  queryOptions({
    queryKey: availabilityQueryKey(date),
    queryFn: () => getAvailability({ data: { date } }),
  });

export const openingHoursQueryOptions = () =>
  queryOptions({
    queryKey: ["opening-hours"],
    queryFn: () => getOpeningHours(),
  });

export const hasDeliveryShiftsQueryOptions = () =>
  queryOptions({
    queryKey: ["has-delivery-shifts"],
    queryFn: () => getHasDeliveryShifts(),
  });

export const getAvailability = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(
    z.object({ date: z.date(), type: z.nativeEnum(OrderTypes).optional() })
  )
  .handler(async ({ context: { userId }, data: input }) => {
    const supabase = createClient();

    // Use SERVER time as source of truth
    const serverNow = new Date();
    const nowInBusinessTz = toZonedTime(serverNow, timezone);
    const dateInBusinessTz = toZonedTime(input.date, timezone);

    // Validate: Only allow checking today's availability
    // Compare dates in business timezone by checking year, month, and day
    if (
      nowInBusinessTz.getFullYear() !== dateInBusinessTz.getFullYear() ||
      nowInBusinessTz.getMonth() !== dateInBusinessTz.getMonth() ||
      nowInBusinessTz.getDate() !== dateInBusinessTz.getDate()
    ) {
      return {
        success: false,
        message: "Can only check availability for today.",
        closedMessages: [],
      };
    }

    const dayOfWeek = dateInBusinessTz.getDay();
    const dayStart = fromZonedTime(startOfDay(dateInBusinessTz), timezone);
    const dayEnd = fromZonedTime(endOfDay(dateInBusinessTz), timezone);

    // Query shifts and individual timeslots in parallel
    const [
      shiftsResult,
      singleShiftsResult,
      individualSlotsResult,
      onlineOrdersResult,
    ] = await Promise.all([
      supabase
        .from("online_order_shift")
        .select(
          "id, start_time, end_time, timeslot_interval_in_minutes, order_type, default_preparation_time_in_minutes, order_timeslot(id, min_advance_in_minutes, preparation_time_in_minutes, start, type, spots, closed, shift_id)"
        )
        .lte("start_date", dayStart.toISOString())
        .or(`end_date.is.null,end_date.gte.${dayEnd.toISOString()}`)
        .contains("repeat_days", [dayOfWeek])
        .eq("user_id", userId)
        .neq("status", Status.trashed),
      supabase
        .from("online_order_shift")
        .select(
          "id, start_time, closed, closed_message, end_time, timeslot_interval_in_minutes, order_type, default_preparation_time_in_minutes, order_timeslot(id, min_advance_in_minutes, preparation_time_in_minutes, start, type, spots, closed, shift_id)"
        )
        .lte("start_date", dayStart.toISOString())
        .or(`end_date.gte.${dayEnd.toISOString()}`)
        .is("repeat_days", null)
        .eq("user_id", userId)
        .neq("status", Status.trashed),
      supabase
        .from("order_timeslot")
        .select(
          "id, shift_id, spots, type, start, closed, min_advance_in_minutes, preparation_time_in_minutes"
        )
        .gte("start", dayStart.toISOString())
        .lte("start", dayEnd.toISOString())
        .is("shift_id", null)
        .eq("user_id", userId),
      supabase
        .from("online_order")
        .select("requested_time, order_status, order_type, timeslot_time")
        .eq("user_id", userId)
        .not("order_status", "in", "(pending_payment,canceled,delivered)")
        .gte("timeslot_time", dayStart.toISOString())
        .lte("timeslot_time", dayEnd.toISOString()),
    ]);

    if (shiftsResult.error) {
      console.error("Could not get shifts", shiftsResult.error);
      return {
        success: false,
        message: "Could not get shifts.",
        closedMessages: [],
      };
    }

    if (singleShiftsResult.error) {
      console.error("Could not get single shifts", singleShiftsResult.error);
      return {
        success: false,
        message: "Could not get single shifts.",
        closedMessages: [],
      };
    }

    if (individualSlotsResult.error) {
      console.error(
        "Could not get individual slots",
        individualSlotsResult.error
      );
      return {
        success: false,
        message: "Could not get individual slots.",
        closedMessages: [],
      };
    }

    if (onlineOrdersResult.error) {
      console.error("Could not get online orders", onlineOrdersResult.error);
      return {
        success: false,
        message: "Could not get online orders.",
        closedMessages: [],
      };
    }

    const singleShifts = singleShiftsResult.data ?? [];

    // Find all closed single shifts for this date
    // Single shifts are already filtered by date range in the query
    const closedSingleShifts = singleShifts.filter((shift) => shift.closed);

    // A shift can be closed with a note for the visitor ("Closed for a private
    // event"), which the page shows instead of the generic closed text. Closing
    // the same order type twice on a day leaves several rows, so only the first
    // note per order type is kept.
    const closedMessages: { orderType: OrderTypes; message: string }[] = [];
    for (const shift of closedSingleShifts) {
      const message = shift.closed_message?.trim();
      if (!message) continue;
      const orderType = shift.order_type as OrderTypes;
      if (closedMessages.some((entry) => entry.orderType === orderType)) {
        continue;
      }
      closedMessages.push({ orderType, message });
    }

    // Get timeslots for matched shifts
    const allRepeatShiftTimeslots =
      shiftsResult.data?.map((s) => s.order_timeslot).flat() ?? [];

    // If we have closed single shifts, replace only the timeslots of their order_types
    const shiftTimeslots =
      closedSingleShifts.length > 0
        ? [
            // Keep repeat shift timeslots that don't match any closed shift's order_type
            ...allRepeatShiftTimeslots.filter(
              (slot) =>
                !closedSingleShifts.some(
                  (closedShift) => closedShift.order_type === slot.type
                )
            ),
            // Add all closed single shifts' timeslots
            ...closedSingleShifts.flatMap(
              (closedShift) => closedShift.order_timeslot ?? []
            ),
          ]
        : allRepeatShiftTimeslots;

    // Preparation time is what the kitchen needs before an order can be handed
    // over, so a slot is only orderable from now + preparation time onwards.
    // It lives on the shift (default_preparation_time_in_minutes) and can be
    // overridden per timeslot (preparation_time_in_minutes).
    const preparationTimeByShiftId = new Map<string, number>();
    const preparationTimeByOrderType = new Map<string, number>();

    [...(shiftsResult.data ?? []), ...singleShifts].forEach((shift) => {
      const preparationTime = shift.default_preparation_time_in_minutes ?? 0;
      preparationTimeByShiftId.set(shift.id, preparationTime);

      // Individual slots are not linked to a shift, so they fall back to the
      // preparation time of the shift that serves their order type. Closed
      // shifts carry a 0 preparation time marker, so they must not win here.
      if (
        !("closed" in shift && shift.closed) &&
        !preparationTimeByOrderType.has(shift.order_type)
      ) {
        preparationTimeByOrderType.set(shift.order_type, preparationTime);
      }
    });

    const getPreparationTimeInMinutes = (slot: {
      shift_id: string | null;
      type: string;
      preparation_time_in_minutes: number;
    }): number => {
      if (slot.preparation_time_in_minutes > 0) {
        return slot.preparation_time_in_minutes;
      }

      if (slot.shift_id) {
        return preparationTimeByShiftId.get(slot.shift_id) ?? 0;
      }

      return preparationTimeByOrderType.get(slot.type) ?? 0;
    };

    const individualSlots = individualSlotsResult.data ?? [];

    const individualSlotMap = new Map(
      individualSlots.map((slot) => [
        getTimeKey(new Date(slot.start), slot.type),
        slot,
      ])
    );

    const filteredShiftTimeslots = shiftTimeslots.filter(
      (slot) =>
        !individualSlotMap.has(getTimeKey(new Date(slot.start), slot.type))
    );

    let allSlots = [...individualSlots, ...filteredShiftTimeslots];

    if (input.type) {
      allSlots = allSlots.filter((slot) => slot.type === input.type);
    }

    const orders = onlineOrdersResult.data ?? [];

    // First, calculate all slot start times so the closing slot can be found
    const slotStartTimes = allSlots.map((slot) => {
      const slotTimeInTimezone = toZonedTime(slot.start, timezone);
      const hours = getHours(slotTimeInTimezone);
      const minutes = getMinutes(slotTimeInTimezone);

      const targetDateTime = set(dateInBusinessTz, {
        hours,
        minutes,
        seconds: 0,
        milliseconds: 0,
      });

      return fromZonedTime(targetDateTime, timezone);
    });

    // Closing time per order type: the latest slot that is still open. Kept per
    // type because delivery and pickup can close at different times, and the
    // closing slot stays orderable until it starts (see below).
    const lastSlotStartTimeByType = new Map<string, number>();

    allSlots.forEach((slot, index) => {
      if (slot.closed) {
        return;
      }

      const startTime = slotStartTimes[index].getTime();
      const currentLast = lastSlotStartTimeByType.get(slot.type);

      if (currentLast === undefined || startTime > currentLast) {
        lastSlotStartTimeByType.set(slot.type, startTime);
      }
    });

    const allTimeslots = allSlots.map((slot) => {
      const slotTimeInTimezone = toZonedTime(slot.start, timezone);
      const hours = getHours(slotTimeInTimezone);
      const minutes = getMinutes(slotTimeInTimezone);

      const targetDateTime = set(dateInBusinessTz, {
        hours,
        minutes,
        seconds: 0,
        milliseconds: 0,
      });

      const slotStartUtc = fromZonedTime(targetDateTime, timezone);
      const nowUtc = new Date();

      const minAdvanceInMinutes = slot.min_advance_in_minutes;
      const lastSlotStartTime = lastSlotStartTimeByType.get(slot.type);
      const isLastSlot =
        lastSlotStartTime !== undefined &&
        isSameMinute(slotStartUtc, new Date(lastSlotStartTime));

      // A slot can never start earlier than now + preparation time, because the
      // kitchen cannot have the order ready by then.
      const isBeforePreparationCutoff = isBefore(
        slotStartUtc,
        addMinutes(nowUtc, getPreparationTimeInMinutes(slot))
      );

      // The closing slot is exempt from both the preparation time and the min
      // advance time: an order must always be placeable while the business is
      // still open, even when preparation runs past closing time (a shift
      // closing at 22:00 with 45 minutes preparation still takes orders at
      // 21:30). Every other slot has to clear both windows.
      const isTimeExpired = isLastSlot
        ? !isBefore(nowUtc, slotStartUtc) // Closing slot: allow while now is before it starts
        : isBeforePreparationCutoff ||
          isAfter(nowUtc, subMinutes(slotStartUtc, minAdvanceInMinutes));

      return {
        id: slot.id,
        start: slotStartUtc,
        closed: slot.closed,
        type: slot.type as OrderTypes,
        shiftId: slot.shift_id,
        spots:
          slot.closed || isTimeExpired
            ? 0
            : Math.max(
                0,
                slot.spots -
                  orders.filter((order) => {
                    if (!order.requested_time) {
                      if (order.timeslot_time) {
                        return (
                          isSameMinute(order.timeslot_time, slotStartUtc) &&
                          order.order_type === slot.type &&
                          order.order_status !== "canceled" &&
                          order.order_status !== "delivered"
                        );
                      }
                      return (
                        order.order_type === slot.type &&
                        order.order_status !== "canceled" &&
                        order.order_status !== "delivered"
                      );
                    }

                    return (
                      isSameMinute(order.requested_time, slotStartUtc) &&
                      order.order_type === slot.type &&
                      order.order_status !== "canceled" &&
                      order.order_status !== "delivered"
                    );
                  }).length
              ),
      };
    });

    return {
      success: true,
      data: allTimeslots.sort((a, b) => a.start.getTime() - b.start.getTime()),
      closedMessages,
    };
  });

export type Availability = NonNullable<
  Awaited<ReturnType<typeof getAvailability>>["data"]
>[number];

/** A note the merchant left when closing an order type for the day. */
export type ClosedMessage = Awaited<
  ReturnType<typeof getAvailability>
>["closedMessages"][number];

export const fetchShifts = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId } }) => {
    const supabase = createClient();

    const shiftsPromise = supabase
      .from("online_order_shift")
      .select(
        "id, closed, repeatDays:repeat_days, repeat, startDate:start_date, endDate:end_date"
      )
      .eq("user_id", userId)
      .eq("status", Status.active);

    const [shiftsResult] = await Promise.all([shiftsPromise]);

    if (shiftsResult.error) {
      console.error(shiftsResult.error);
      throw new Error("Could not get shifts.");
    }

    const shifts = shiftsResult.data;

    return shifts.map((shift) => ({
      ...shift,
      startDate: parseISO(shift.startDate),
      endDate: shift.endDate ? parseISO(shift.endDate) : undefined,
    }));
  });

export const getOpeningHours = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId } }) => {
    const supabase = createClient();

    const { data: shifts, error: shiftsError } = await supabase
      .from("online_order_shift")
      .select("start_time, end_time, order_type, repeat_days")
      .lte("start_date", startOfDay(new Date()).toISOString())
      .eq("status", Status.active)
      .or(`end_date.is.null,end_date.gte.${new Date().toISOString()}`)
      .eq("user_id", userId);

    if (shiftsError) {
      console.error("Could not get shifts", shiftsError);
      return {
        success: false,
        message: "Could not get shifts.",
      };
    }

    const days: Record<
      number,
      Partial<Record<OrderTypes, { startTime: Date; endTime: Date }>>
    > = {
      0: {},
      1: {},
      2: {},
      3: {},
      4: {},
      5: {},
      6: {},
    };

    shifts.forEach((shift) => {
      const startTime = parseISO(shift.start_time);
      const endTime = parseISO(shift.end_time);
      const orderType = shift.order_type as OrderTypes;

      (shift.repeat_days ?? []).forEach((day: number) => {
        if (day >= 0 && day <= 6 && !days[day][orderType]) {
          days[day][orderType] = { startTime, endTime };
        }
      });
    });

    return {
      success: true,
      data: days,
    };
  });

export type OpeningHours = Awaited<ReturnType<typeof getOpeningHours>>;

export type Shift = Awaited<ReturnType<typeof fetchShifts>>[number];

// Whether the business offers delivery at all, i.e. it has at least one active
// delivery shift configured. Used to hide the delivery order type entirely when
// no delivery shifts exist (fails open so a transient error keeps delivery visible).
export const getHasDeliveryShifts = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId } }) => {
    const supabase = createClient();

    const { count, error } = await supabase
      .from("online_order_shift")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("order_type", OrderTypes.delivery)
      .eq("status", Status.active);

    if (error) {
      console.error("Could not check delivery shifts", error);
      throw new Error("Could not check delivery shifts.");
    }

    return { hasDeliveryShifts: (count ?? 0) > 0 };
  });
